import {
  cloneElement,
  forwardRef,
  type InputHTMLAttributes,
  type ReactElement,
} from 'react';
import { LoaderCircle } from 'lucide-react';

import { inputStyle } from './input';

export type NumberInputProps = InputHTMLAttributes<HTMLInputElement> & {
  loading?: boolean;
  error?: boolean;
  hint?: string;
  inline?: boolean;
  leftIcon?: ReactElement<{ className?: string }>;
  rightIcon?: ReactElement<{ className?: string }>;
  value?: number;
  onChange?: (value: number) => void;
};

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function (
  {
    loading = false,
    error = false,
    disabled = false,
    inline = false,
    hint,
    leftIcon,
    rightIcon,
    className,
    value,
    onChange,
    ...props
  },
  ref,
) {
  const style = numberInputStyle({
    error,
    loading,
    disabled,
    inline,
    withLeft: Boolean(leftIcon),
    withRight: Boolean(rightIcon),
  });

  return (
    <div>
      <div className={style.container()}>
        <input
          {...props}
          value={value?.toString() ?? ''}
          onChange={function (e) {
            const nb = Number(e.currentTarget.value);
            if (Number.isNaN(nb)) {
              return;
            }

            onChange?.(nb);
          }}
          ref={ref}
          disabled={disabled || loading}
          className={style.input({ className })}
          type="number"
        />
        <div className={style.inputLoader()}>
          <LoaderCircle className={style.inputLoaderIcon()} />
        </div>
        {leftIcon ? (
          <div className={style.inputLeft()}>
            {cloneElement(leftIcon, {
              className: style.inputIcon({
                className: leftIcon.props.className,
              }),
            })}
          </div>
        ) : null}
        {rightIcon ? (
          <div className={style.inputRight()}>
            {cloneElement(rightIcon, {
              className: style.inputIcon({
                className: rightIcon.props.className,
              }),
            })}
          </div>
        ) : null}
      </div>
      {hint ? <div className={style.helperText()}>{hint}</div> : null}
    </div>
  );
});

export const numberInputStyle = inputStyle;

NumberInput.displayName = 'NumberInput';
export { NumberInput };
